// src/pages/admin/CheckPreview.jsx
import { useCallback, useMemo } from "react";
import { Loading, PreviewField, PreviewInput, PREVIEW_INPUT_CLASSES, fmtLineAmount } from "@common";
import {
  useBankAccounts,
  useClinicSubsidiaryMap,
  useNetsuiteAccounts,
  useNetsuiteSubsidiaries,
  useNetsuiteVendors,
} from "@api/hooks";
import { NETSUITE_ACCOUNT_IDS } from "@utils";

const DEFAULT_ACCOUNT_ID = String(NETSUITE_ACCOUNT_IDS.RBC_SF_CORPORATION_9411);
const DEFAULT_SUBSIDIARY_ID = String(NETSUITE_ACCOUNT_IDS.SMILES_FIRST_CORPORATION);

const listOf = (data) => data?.items ?? data?.results ?? (Array.isArray(data) ? data : []);

/**
 * Summary shown in the confirmation modal before sending a Check to NetSuite.
 * Read-only unless `onChange` is given, in which case the header fields —
 * including the Bank Account and Subsidiary selects — are editable.
 *
 * Bank Account and Subsidiary default to SFC's RBC [9411] account under Smiles
 * First Corporation. The alternatives are THIS clinic's own NetSuite bank
 * account(s) and its own subsidiary — so a check can be drawn on
 * the clinic instead of the parent. Switching the bank account moves the
 * subsidiary with it; the subsidiary can still be flipped on its own.
 *
 * Props:
 *   preview = { clinic, row, payload }
 *   payload = {
 *     subsidiary:{id}, account:{id}, entity:{id}, tranDate, memo,
 *     expense: { items: [{ account:{id}, amount, memo }] }
 *   }
 */
export default function CheckPreview({ preview, onChange }) {
  const items = useMemo(() => preview?.payload?.expense?.items ?? [], [preview]);

  // Unique account ids referenced (bank account + expense line accounts)
  const accountIds = useMemo(() => {
    const seen = new Set();
    const out = [];
    const push = (id) => {
      if (id === null || id === undefined || id === "") return;
      const key = String(id);
      if (seen.has(key)) return;
      seen.add(key);
      out.push(key);
    };
    push(preview?.payload?.account?.id);
    for (const it of items) push(it?.account?.id);
    return out;
  }, [items, preview]);

  const accountsQuery = useNetsuiteAccounts(
    { ids: accountIds },
    { enabled: accountIds.length > 0 },
  );

  const clinicCode = String(preview?.clinic ?? "").toUpperCase();

  // Name lookup only — the selects offer just SFC and this clinic, never the
  // whole subsidiary list. One shared cached query for every preview in a batch.
  const subsidiaryId = preview?.payload?.subsidiary?.id ?? null;
  const subsidiaryQuery = useNetsuiteSubsidiaries();
  const subsidiaryNameById = useMemo(() => {
    const map = {};
    for (const s of listOf(subsidiaryQuery.data)) {
      const id = s?.id ?? s?.ID;
      if (id !== null && id !== undefined) map[String(id)] = s?.name ?? s?.fullname ?? "";
    }
    return map;
  }, [subsidiaryQuery.data]);
  const subsidiaryName =
    subsidiaryId != null ? (subsidiaryNameById[String(subsidiaryId)] ?? "") : "";

  // This clinic's own bank account + subsidiary, the only alternative to the
  // SFC default. Both are whole-table lookups (one request each, shared across
  // every preview in a bulk batch) filtered down to `clinicCode` here, rather
  // than a per-clinic request per open preview. Fetched only when editable.
  // The account comes from the `bank_accounts` registry (the same source as
  // Daily Bank Balance), so admin-added accounts show up here too.
  const editable = !!onChange;
  const clinicBankQuery = useBankAccounts(
    { has_clinic: true, status: "active", account_type: "bank" },
    { enabled: editable },
  );
  const clinicSubsidiaryQuery = useClinicSubsidiaryMap({ enabled: editable });

  const clinicSubsidiaryId = useMemo(() => {
    const match = listOf(clinicSubsidiaryQuery.data).find(
      (r) => String(r?.clinic ?? "").toUpperCase() === clinicCode,
    );
    const sid = match?.netsuite_subsidiary_id;
    return sid === null || sid === undefined || sid === "" ? null : String(sid);
  }, [clinicSubsidiaryQuery.data, clinicCode]);

  // A clinic can own several bank accounts, so every one with a NetSuite id is
  // offered (the default account is skipped — it already has its own option).
  const clinicAccounts = useMemo(() => {
    const out = [];
    for (const r of listOf(clinicBankQuery.data)) {
      if (String(r?.clinic_code ?? "").toUpperCase() !== clinicCode) continue;
      const aid = r?.netsuite_account_id;
      if (aid === null || aid === undefined || aid === "") continue;
      const id = String(aid);
      if (id === DEFAULT_ACCOUNT_ID || out.some((a) => a.id === id)) continue;
      const sid = r?.netsuite_subsidiary_id;
      out.push({
        id,
        name: r?.label ?? "",
        subsidiaryId: sid === null || sid === undefined || sid === "" ? null : String(sid),
      });
    }
    return out;
  }, [clinicBankQuery.data, clinicCode]);

  // The two are picked as a pair: the default account belongs to SFC, the clinic
  // account to the clinic's subsidiary. The Subsidiary select can still be
  // flipped on its own afterwards.
  const handleAccountChange = useCallback(
    (nextId) => {
      const patch = { account: { id: String(nextId) } };
      const nextSubsidiary =
        String(nextId) === DEFAULT_ACCOUNT_ID
          ? DEFAULT_SUBSIDIARY_ID
          : (clinicAccounts.find((a) => a.id === String(nextId))?.subsidiaryId ??
            clinicSubsidiaryId);
      if (nextSubsidiary) patch.subsidiary = { id: nextSubsidiary };
      onChange?.(patch);
    },
    [clinicAccounts, clinicSubsidiaryId, onChange],
  );

  // Vendor/entity lookup — uses the netsuite vendors endpoint
  const entityId = preview?.payload?.entity?.id ?? null;
  const vendorQuery = useNetsuiteVendors({ id: entityId }, { enabled: !!entityId });
  const vendorName = useMemo(() => {
    const list = listOf(vendorQuery.data);
    const match = list.find((v) => String(v?.id ?? v?.ID) === String(entityId));
    return match?.fullname ?? match?.entitytitle ?? "";
  }, [vendorQuery.data, entityId]);

  // id -> displayname map for accounts
  const accountNameById = useMemo(() => {
    const list = listOf(accountsQuery.data);
    const map = {};
    for (const a of list) {
      const id = a?.id ?? a?.ID;
      const name = a?.displaynamewithhierarchy ?? a?.displayNameWithHierarchy;
      if (id !== undefined && id !== null) map[String(id)] = name ?? "";
    }
    return map;
  }, [accountsQuery.data]);

  if (!preview) return null;
  const { clinic, row, payload } = preview;

  const total = items.reduce((s, it) => s + (Number(it.amount) || 0), 0);
  const accountsLoading = accountsQuery.isLoading;

  const bankAccountId = payload?.account?.id;
  const bankAccountName =
    (bankAccountId ? accountNameById[String(bankAccountId)] : "") ||
    clinicAccounts.find((a) => a.id === String(bankAccountId))?.name ||
    "";

  return (
    <div className="space-y-4">
      {/* Header fields */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 rounded-md border border-palette-gray-divider bg-palette-gray-soft p-3">
        <PreviewField label="Clinic" value={clinic} />
        {onChange ? (
          <PreviewField label="Bank Account">
            <div className="flex flex-col gap-1">
              <select
                value={bankAccountId ?? ""}
                onChange={(e) => handleAccountChange(e.target.value)}
                className={`${PREVIEW_INPUT_CLASSES} w-full`}
              >
                <option value={DEFAULT_ACCOUNT_ID}>
                  {(accountNameById[DEFAULT_ACCOUNT_ID] ||
                    "Bank Balance : RBC S.F. Corporation - [9411]") + " (default)"}
                </option>
                {clinicAccounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {`${a.name || `Account #${a.id}`} (${clinicCode})`}
                  </option>
                ))}
                {/* Whatever is on the payload stays selectable even if it is
                    neither option (e.g. a value carried over from an edit). */}
                {bankAccountId &&
                  String(bankAccountId) !== DEFAULT_ACCOUNT_ID &&
                  !clinicAccounts.some((a) => a.id === String(bankAccountId)) && (
                    <option value={String(bankAccountId)}>
                      {bankAccountName || `Account #${bankAccountId}`}
                    </option>
                  )}
              </select>
              <span className="text-xs text-palette-gray-badge">
                {clinicBankQuery.isLoading
                  ? `Loading ${clinicCode}'s bank account\u2026`
                  : clinicAccounts.length > 0
                    ? `Choosing ${clinicCode} also switches the subsidiary to ${clinicCode}'s.`
                    : `${clinicCode} has no NetSuite bank account configured.`}
              </span>
            </div>
          </PreviewField>
        ) : (
          <PreviewField
            label="Bank Account"
            value={
              bankAccountName
                ? bankAccountName
                : accountsLoading
                  ? "Loading\u2026"
                  : (bankAccountId ?? "\u2014")
            }
          />
        )}
        {onChange ? (
          <PreviewField label="Subsidiary">
            <div className="flex flex-col gap-1">
              <select
                value={subsidiaryId ?? ""}
                onChange={(e) => onChange({ subsidiary: { id: e.target.value } })}
                className={`${PREVIEW_INPUT_CLASSES} w-full`}
              >
                <option value={DEFAULT_SUBSIDIARY_ID}>
                  {`${subsidiaryNameById[DEFAULT_SUBSIDIARY_ID] || "Smiles First Corporation"} (default)`}
                </option>
                {clinicSubsidiaryId && clinicSubsidiaryId !== DEFAULT_SUBSIDIARY_ID && (
                  <option value={clinicSubsidiaryId}>
                    {`${subsidiaryNameById[clinicSubsidiaryId] || `Subsidiary #${clinicSubsidiaryId}`} (${clinicCode})`}
                  </option>
                )}
                {subsidiaryId != null &&
                  String(subsidiaryId) !== "" &&
                  String(subsidiaryId) !== DEFAULT_SUBSIDIARY_ID &&
                  String(subsidiaryId) !== clinicSubsidiaryId && (
                    <option value={String(subsidiaryId)}>
                      {subsidiaryName || `Subsidiary #${subsidiaryId}`}
                    </option>
                  )}
              </select>
              {clinicSubsidiaryQuery.isLoading ? (
                <span className="text-xs text-palette-gray-badge">
                  {`Loading ${clinicCode}'s subsidiary\u2026`}
                </span>
              ) : clinicSubsidiaryId ? null : (
                <span className="text-xs text-palette-gray-badge">
                  {`${clinicCode} has no NetSuite subsidiary configured.`}
                </span>
              )}
            </div>
          </PreviewField>
        ) : (
          <PreviewField
            label="Subsidiary"
            value={
              subsidiaryName
                ? subsidiaryName
                : subsidiaryQuery.isLoading
                  ? "Loading\u2026"
                  : (payload?.subsidiary?.id ?? "\u2014")
            }
          />
        )}
        {onChange ? (
          <PreviewField label="Payee (Vendor)">
            <div className="flex flex-col gap-1">
              <PreviewInput
                type="text"
                inputMode="numeric"
                value={payload?.entity?.id ?? ""}
                onChange={(e) =>
                  onChange({ entity: { id: e.target.value.replace(/[^0-9]/g, "") } })
                }
                placeholder="Vendor ID"
                className="text-sm font-medium text-palette-gray-text border border-palette-gray-border rounded px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-palette-indigo-label"
              />
              <span className="text-xs text-palette-gray-badge">
                {vendorQuery.isFetching
                  ? "Looking up vendor\u2026"
                  : vendorName
                    ? `${vendorName}${entityId ? ` (#${entityId})` : ""}`
                    : entityId
                      ? "No vendor found for this ID"
                      : "Enter a vendor ID"}
              </span>
            </div>
          </PreviewField>
        ) : (
          <PreviewField
            label="Payee (Vendor)"
            value={
              vendorName
                ? `${vendorName}${entityId ? ` (#${entityId})` : ""}`
                : vendorQuery.isLoading
                  ? "Loading\u2026"
                  : (row?.practitioner_name ?? entityId ?? "\u2014")
            }
          />
        )}
        {onChange ? (
          <PreviewField label="Transaction Date">
            <PreviewInput
              type="date"
              value={payload?.tranDate ?? ""}
              onChange={(e) => onChange({ tranDate: e.target.value })}
              className="text-sm font-medium text-palette-gray-text border border-palette-gray-border rounded px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-palette-indigo-label"
            />
          </PreviewField>
        ) : (
          <PreviewField label="Transaction Date" value={payload?.tranDate} />
        )}
        <PreviewField label="Practitioner" value={row?.practitioner_name ?? "—"} />
        <div className="sm:col-span-2">
          <PreviewField label="Memo" value={payload?.memo} />
        </div>
        <div className="sm:col-span-2">
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={!!payload?.toBePrinted}
              onChange={(e) => onChange && onChange({ toBePrinted: e.target.checked })}
              disabled={!onChange}
              className="h-4 w-4 rounded border-palette-gray-border text-palette-indigo-badge focus:ring-palette-indigo-label"
            />
            <span className="text-sm font-medium text-palette-gray-text">To Be Printed</span>
          </label>
        </div>
      </div>

      {/* Expense line items */}
      <div>
        <div className="mb-1 flex items-center justify-between">
          <span className="text-sm font-semibold text-palette-gray-heading">
            Expense Items ({items.length})
          </span>
        </div>
        <div className="max-h-[28rem] overflow-y-auto rounded-md border border-palette-gray-divider">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-palette-gray-subtle text-palette-gray-heading">
              <tr>
                <th className="text-left px-3 py-3 font-semibold">Account</th>
                <th className="text-right px-3 py-3 font-semibold">Amount</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={2} className="px-3 py-4 text-center text-palette-gray-secondary">
                    No expense items.
                  </td>
                </tr>
              ) : (
                items.map((it, idx) => {
                  const id = it.account?.id;
                  const name = id != null ? accountNameById[String(id)] : "";
                  return (
                    <tr key={idx} className="border-t border-palette-gray-subtle">
                      <td className="px-3 py-3">
                        {name ? (
                          <span className="text-palette-gray-text">{name}</span>
                        ) : accountsLoading ? (
                          <Loading size="sm" text="Loading account..." />
                        ) : (
                          <span className="font-mono text-xs text-palette-gray-secondary">
                            {id ?? "—"}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-3 text-right">
                        {onChange ? (
                          <input
                            type="number"
                            step="0.01"
                            value={it.amount ?? ""}
                            onChange={(e) => {
                              const raw = e.target.value;
                              const next = raw === "" ? "" : Number(raw);
                              const newItems = items.map((row, i) =>
                                i === idx ? { ...row, amount: next } : row,
                              );
                              onChange({ expense: { ...payload.expense, items: newItems } });
                            }}
                            className="w-32 text-right text-sm font-medium text-palette-gray-text border border-palette-gray-border rounded px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-palette-indigo-label"
                          />
                        ) : it.amount != null ? (
                          fmtLineAmount(it.amount)
                        ) : (
                          ""
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
            <tfoot className="bg-palette-gray-soft">
              <tr className="border-t border-palette-gray-divider">
                <td className="px-3 py-3 text-right font-semibold">Total</td>
                <td className="px-3 py-3 text-right font-semibold">{fmtLineAmount(total)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <p className="text-xs text-palette-gray-secondary">
        This will create a new Check in NetSuite. Please review the values above before confirming.
      </p>
    </div>
  );
}
